import type { UsersTranslations } from './types'

/**
 * English translations for Users domain
 */
export const enTranslations: UsersTranslations = {
  // Page/Section level
  title: "Users",
  subtitle: "Manage users and permissions",

  // Actions specific to domain
  addUser: "Add User",
  editUser: "Edit User",
  deleteUser: "Delete User",

  // Search and filters
  searchPlaceholder: "Search users...",
  noItemsFound: "No users found",
  noItemsDescription: "You haven't added any users yet. Add your first user to get started.",
  addFirst: "Add Your First User",

  // Form section
  form: {
    name: "User Name",
    namePlaceholder: "Enter user name",
    status: "Status",

    validation: {
      nameRequired: "User name is required",
    }
  },

  // Status and states specific to domain
  status: {
    active: "Active",
    inactive: "Inactive",
  },

  // Toast messages for domain operations
  messages: {
    createSuccess: "User created successfully",
    updateSuccess: "User updated successfully",
    deleteSuccess: "User deleted successfully",
    deleteConfirm: "Are you sure you want to delete this user?",
  },

  // Table headers if applicable
  table: {
    name: "Name",
    status: "Status",
  },

  // Top-level convenience keys
  name: "Name",
  namePlaceholder: "Enter user name",
  statusLabel: "Status",
  nameRequired: "User name is required",
  active: "Active",
  inactive: "Inactive",
  createSuccess: "User created successfully",
  updateSuccess: "User updated successfully",
  deleteSuccess: "User deleted successfully",
  deleteConfirm: "Are you sure you want to delete this user?",

  // Attempt lockout
  unlockAttempts: "Unlock Account (Attempts)",
  attemptBlocked: "Locked (Too Many Attempts)",

  // Customer Onboarding
  onboarding: {
    title: 'Customer Onboarding',
    subtitle: 'Register existing SAP customers as portal users',
    modeManual: 'Manual Entry',
    modeExcel: 'Excel Import',
    successMessage: 'Customer registered successfully',
    errorMessage: 'Registration failed. Please try again.',
    submit: 'Register Customer',
    submitting: 'Registering...',
    errors: {
      emailRequired: 'Email is required',
      nameRequired: 'Company / full name is required',
      mobilePhoneRequired: 'Mobile phone is required',
      passwordMin: 'Password must be at least 8 characters',
      cityRequired: 'City is required',
      countryRequired: 'Country is required',
      phoneInvalidFormat: 'Mobile phone must contain only numbers and an optional + prefix',
      phoneInvalidLength: 'Mobile phone must be between 10 and 15 digits',
      vatNumberRequired: 'VAT number is required',
      crNumberRequired: 'CR number is required',
      addressLine1Required: 'Street address is required',
      regionRequired: 'Region is required',
      postalCodeRequired: 'Postal code is required',
      sapCustomerGroupRequired: 'SAP customer group is required',
      registerDisabled: 'Registration is currently disabled',
      emailTaken: 'Email is already registered',
      phoneTaken: 'Mobile phone is already registered',
      sapBusinessPartnerNumberTaken: 'SAP Business Partner number is already registered',
      vatCertificateRequired: 'VAT certificate is required (or enter an SAP Business Partner #)',
      crDocumentRequired: 'CR document is required (or enter an SAP Business Partner #)',
    },
    steps: {
      account: 'Account',
      business: 'Business',
      address: 'Address',
    },
    form: {
      nameAndCompany: 'Company / Full Name',
      nameAndCompanyPlaceholder: 'Enter company or full name',
      email: 'Email Address',
      emailPlaceholder: 'Enter email address',
      mobilePhone: 'Mobile Phone',
      password: 'Password',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      regeneratePassword: 'Regenerate',
      autoGeneratedBadge: 'Auto-generated',
      sapBusinessPartnerNumber: 'SAP Business Partner #',
      sapCustomerGroup: 'SAP Customer Group',
      vatNumber: 'VAT Number',
      crNumber: 'CR Number',
      addressLine1: 'Street Address',
      addressLine1Placeholder: 'Enter street address',
      city: 'City',
      region: 'Region',
      regionPlaceholder: 'Select region',
      postalCode: 'Postal Code',
      country: 'Country',
    },
    excel: {
      dropzone: 'Drop your Excel file here',
      dropzoneHint: 'Accepts .xlsx, .xls files from SAP exports',
      browseFile: 'Browse File',
      parseError: 'Failed to parse file. Make sure it is a valid Excel file.',
      customersLoaded: 'customers loaded',
      emailsGenerated: 'emails generated',
      succeeded: 'succeeded',
      failed: 'failed',
      clear: 'Clear',
      registerAll: 'Register All',
      retryFailed: 'Retry Failed',
      bulkSuccess: '{{count}} customers registered successfully',
      downloadResults: 'Download Results',
      col: {
        sap: 'SAP #',
        name: 'Name',
        email: 'Email',
        phone: 'Phone',
        city: 'City',
        status: 'Status',
        password: 'Password',
      },
      status: {
        pending: 'Pending',
        submitting: 'Registering...',
        success: 'Success',
        error: 'Failed',
      },
    },
  },
}
